import { useCallback, useEffect, useMemo, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { listAgent, type UserInfo } from "@/api/agent";
import { listMaterial, type MaterialItem } from "@/api/materialInfo";
import {
  calcTimeRange,
  exportActivityLogs,
  listActivityLogs,
  resolveTimeRange,
  validateTimeRange,
  type ActivityLogItem,
  type ActivityLogQuery,
} from "@/api/activityLog";
import { type TimeFilter } from "@/api/dashboard";
import { getTree, type CategoryNode } from "@/api/materialCategory";

const TIME_PRESETS: { value: TimeFilter; label: string }[] = [
  { value: "today", label: "今日" },
  { value: "7days", label: "7天" },
  { value: "30days", label: "30天" },
  { value: "1year", label: "1年" },
  { value: "all", label: "全部" },
];

function formatActionTime(ts: number): string {
  if (!ts) return "—";
  const d = new Date(ts);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  return `${y}-${m}-${day} ${h}:${min}:${s}`;
}

function msToDateInput(ms: number): string {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** 从日期输入或预设计算 [startTime, endTime) — 见 activityLog.resolveTimeRange */
function getPageTimeRange(
  startDate: string,
  endDate: string,
  timePreset: TimeFilter
): [number, number] {
  return resolveTimeRange(startDate, endDate, timePreset);
}

export default function ActivityLogsPage() {
  const [list, setList] = useState<ActivityLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const [timePreset, setTimePreset] = useState<TimeFilter>("30days");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [actionType, setActionType] = useState<"" | "VIEW" | "DOWNLOAD">("");
  const [keyword, setKeyword] = useState("");
  const [userId, setUserId] = useState("");
  const [materialId, setMaterialId] = useState("");

  const [agentOptions, setAgentOptions] = useState<UserInfo[]>([]);
  const [materialOptions, setMaterialOptions] = useState<MaterialItem[]>([]);

  const [categoryTree, setCategoryTree] = useState<CategoryNode[]>([]);

  const categoryLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const primary of categoryTree) {
      map[primary.id] = primary.label;
      for (const sub of primary.subCategories) {
        map[sub.id] = sub.label;
        for (const model of sub.models || []) {
          map[model.id] = model.label;
        }
      }
    }
    return map;
  }, [categoryTree]);

  const resolveCategoryLabel = (id: string) => {
    if (!id) return "—";
    const label = categoryLabelMap[id] || id;
    return label === "E-learning" ? "培训" : label;
  };

  const buildQuery = useCallback(
    (page: number, range?: [number, number]): ActivityLogQuery => {
      const [startTime, endTime] = range ?? getPageTimeRange(startDate, endDate, timePreset);
      return {
        startTime,
        endTime,
        userId: userId || undefined,
        materialId: materialId || undefined,
        actionType,
        keyword: keyword.trim() || undefined,
        pageNumber: page,
        pageSize,
      };
    },
    [actionType, endDate, keyword, materialId, pageSize, startDate, timePreset, userId]
  );

  const fetchList = useCallback(
    async (page: number, range?: [number, number]) => {
      const query = buildQuery(page, range);
      const rangeErr = validateTimeRange(query.startTime, query.endTime);
      if (rangeErr) {
        setError(rangeErr);
        setList([]);
        setTotal(0);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const result = await listActivityLogs(query);
        setList(result.list);
        setTotal(result.total);
        setPageNumber(result.pageNumber);
      } catch (e) {
        setError(e instanceof Error ? e.message : "加载失败");
      } finally {
        setLoading(false);
      }
    },
    [buildQuery]
  );

  useEffect(() => {
    getTree().then(setCategoryTree).catch(() => {});
    listAgent({ pageNumber: 1, pageSize: 200 }).then((r) => setAgentOptions(r.list)).catch(() => {});
    listMaterial({ pageNumber: 1, pageSize: 200 }).then((r) => setMaterialOptions(r.list)).catch(() => {});

    const [start, end] = calcTimeRange("30days");
    setStartDate(msToDateInput(start));
    setEndDate(msToDateInput(end - 86400000));
    fetchList(1, [start, end]);
  }, []);

  const applyPreset = (preset: TimeFilter) => {
    const [start, end] = calcTimeRange(preset);
    setTimePreset(preset);
    setStartDate(msToDateInput(start));
    setEndDate(msToDateInput(end - 86400000));
    fetchList(1, [start, end]);
  };

  const handleSearch = () => fetchList(1);

  const handleReset = () => {
    const [start, end] = calcTimeRange("30days");
    setTimePreset("30days");
    setActionType("");
    setKeyword("");
    setUserId("");
    setMaterialId("");
    setStartDate(msToDateInput(start));
    setEndDate(msToDateInput(end - 86400000));
    setError("");
    fetchList(1, [start, end]);
  };

  const handleExport = async () => {
    const query = buildQuery(1);
    const rangeErr = validateTimeRange(query.startTime, query.endTime);
    if (rangeErr) {
      setError(rangeErr);
      return;
    }

    setExporting(true);
    setError("");
    try {
      const { blob, filename } = await exportActivityLogs({
        startTime: query.startTime,
        endTime: query.endTime,
        userId: query.userId,
        materialId: query.materialId,
        actionType: query.actionType,
        keyword: query.keyword,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "导出失败");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <AdminLayout title="活动明细" subtitle={`共 ${total.toLocaleString()} 条操作记录`}>
      <div className="space-y-4">
        {error && (
          <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 mr-1">时间范围</span>
            <div className="inline-flex bg-gray-200 rounded-lg p-1">
              {TIME_PRESETS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => applyPreset(opt.value)}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-all cursor-pointer ${
                    timePreset === opt.value
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">开始日期</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">结束日期</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">操作类型</label>
              <select
                value={actionType}
                onChange={(e) => setActionType(e.target.value as "" | "VIEW" | "DOWNLOAD")}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm min-w-[120px]"
              >
                <option value="">全部</option>
                <option value="VIEW">浏览</option>
                <option value="DOWNLOAD">下载</option>
              </select>
            </div>
            <div className="min-w-[160px]">
              <label className="block text-xs font-semibold text-gray-500 mb-1">代理商</label>
              <select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">全部</option>
                {agentOptions.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[180px]">
              <label className="block text-xs font-semibold text-gray-500 mb-1">资料</label>
              <select
                value={materialId}
                onChange={(e) => setMaterialId(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm max-w-[220px]"
              >
                <option value="">全部</option>
                {materialOptions.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-semibold text-gray-500 mb-1">关键词</label>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="代理商姓名或资料标题"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSearch}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg cursor-pointer disabled:opacity-50"
                style={{ background: "#0069BD" }}
              >
                查询
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer disabled:opacity-50"
              >
                重置
              </button>
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting || loading}
                className="px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
              >
                <i className={`ri-file-excel-2-line ${exporting ? "animate-pulse" : ""}`}></i>
                {exporting ? "导出中..." : "导出 Excel"}
              </button>
            </div>
          </div>
        </div>

        <div
          className="bg-white rounded-xl border border-gray-100 overflow-hidden"
          style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/95">
                  {["操作时间", "类型", "代理商", "资料", "分类", "地区"].map((h) => (
                    <th
                      key={h}
                      className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 6 }).map((_, j) => (
                        <td key={j} className="px-4 py-3">
                          <div className="h-4 bg-gray-100 rounded animate-pulse w-20" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : list.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-gray-400 text-sm">
                      暂无活动明细
                    </td>
                  </tr>
                ) : (
                  list.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                        {formatActionTime(row.actionTime)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                            row.actionType === "VIEW"
                              ? "bg-blue-50 text-blue-700 border border-blue-100"
                              : "bg-amber-50 text-amber-700 border border-amber-100"
                          }`}
                        >
                          {row.actionType === "VIEW" ? "浏览" : "下载"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs font-semibold text-gray-800 whitespace-nowrap">
                        {row.agentName || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-700 max-w-[200px] truncate">
                        {row.materialTitle || "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="px-1.5 py-0.5 text-xs rounded-full border font-medium bg-gray-100 text-gray-500 border-gray-200">
                          {resolveCategoryLabel(row.category)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {row.agentRegion || "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                第 {pageNumber} / {totalPages} 页，共 {total.toLocaleString()} 条
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={pageNumber <= 1 || loading}
                  onClick={() => fetchList(pageNumber - 1)}
                  className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  上一页
                </button>
                <button
                  type="button"
                  disabled={pageNumber >= totalPages || loading}
                  onClick={() => fetchList(pageNumber + 1)}
                  className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  下一页
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
